import { apiService } from './apiService';
import { EcoCashClaimResponse, EcoCashReceiptResponse } from '../types/payments';

export interface AdminStats {
  total_users: { value: string; trend: string };
  active_subjects: { value: string; trend: string };
  daily_chats: { value: string; trend: string };
  user_growth: { value: string; trend: string };
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  grade: string;
  school_level: string;
  created_at: string;
}

export type UserType = 'ADMIN' | 'USER';
export type SchoolLevel = 'primary' | 'secondary';
export type PrimaryGrade = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7';
export type SecondaryGrade = 'form1' | 'form2' | 'form3' | 'form4' | 'form5' | 'form6';
export type Grade = PrimaryGrade | SecondaryGrade;

export interface CreateUserData {
  email?: string;
  whatsapp?: string;
  first_name: string;
  last_name: string;
  password: string;
  user_type: string;
  school_level?: string;
  grade?: string;
}

export interface Subject {
  id: string;
  name: string;
  is_archived: boolean;
  created_at: string;
}

interface CreateSubjectData {
  name: string;
  is_archived?: boolean;
}

export interface MerchantDevice {
  id: string;
  device_label: string;
  platform: string;
  is_active: boolean;
  last_seen_at?: string;
  last_heartbeat_at?: string;
  last_sync_at?: string;
  last_receipt_at?: string;
  last_error?: string;
  last_app_version?: string;
  last_queue_size?: number;
  last_battery_level?: number;
  last_is_online?: boolean;
  last_sms_permission_granted?: boolean;
  last_notification_access_enabled?: boolean;
  last_ignoring_battery_optimizations?: boolean;
  health_status: string;
  created_at: string;
  enrollment_code?: string;
  enrollment_code_expires_at?: string;
}

export interface CreateMerchantDeviceData {
  device_label: string;
  device_fingerprint?: string;
  enrollment_code?: string;
  enrollment_code_expires_at?: string;
  extra_data?: Record<string, unknown>;
}

export interface EcoCashAdminAlert {
  id: string;
  alert_type: string;
  severity: string;
  title: string;
  detail: string;
  entity_type: string;
  entity_id: string;
  created_at: string;
}

export interface RotateMerchantCredentialsResponse {
  device_id: string;
  api_key: string;
  hmac_secret: string;
  rotated_at: string;
}

export interface CreateManualEcoCashReceiptData {
  ingest_key: string;
  source: 'sms' | 'notification' | 'api';
  sender_address?: string;
  source_package?: string;
  merchant_app_version?: string;
  raw_message: string;
  device_received_at?: string;
  received_at?: string;
  extra_data?: Record<string, unknown>;
}

export interface PendingUser {
  id: string;
  email?: string;
  whatsapp?: string;
  first_name: string;
  last_name: string;
  school_level?: SchoolLevel;
  grade?: Grade;
  created_at: string;
}

export const adminService = {
  getStats: async (): Promise<AdminStats> => {
    const response = await apiService.request<AdminStats>({
      url: '/admin/stats',
      method: 'GET'
    });
    return response.data;
  },
  createUser: async (userData: CreateUserData): Promise<User> => {
    const response = await apiService.request<User>({
      method: 'POST',
      url: '/admin/users',
      data: userData
    });
    return response.data;
  },
  getUsers: async (): Promise<User[]> => {
    const response = await apiService.request<User[]>({
      url: '/admin/users',
      method: 'GET'
    });
    return response.data;
  },

  deleteUser: async (userId: string) => {
    const response = await apiService.request({
      url: `/admin/users/${userId}`,
      method: 'DELETE'
    });
    return response.data;
  },

  getSubjects: async (): Promise<Subject[]> => {
    const response = await apiService.request<Subject[]>({
      url: '/admin/subjects',
      method: 'GET'
    });
    return response.data;
  },

  deleteSubject: async (subjectId: string) => {
    const response = await apiService.request({
      url: `/admin/subjects/${subjectId}`,
      method: 'DELETE'
    });
    return response.data;
  },

  createSubject: async (subjectData: CreateSubjectData): Promise<Subject> => {
    const response = await apiService.request<Subject>({
      method: 'POST',
      url: '/admin/subjects',
      data: subjectData
    });
    return response.data;
  },

  getPendingUsers: async (): Promise<PendingUser[]> => {
    const response = await apiService.request<PendingUser[]>({
      url: '/admin/pending-users',
      method: 'GET'
    });
    return response.data;
  },

  approvePendingUser: async (userId: string): Promise<User> => {
    const response = await apiService.request<User>({
      url: `/admin/pending-users/${userId}/approve`,
      method: 'POST'
    });
    return response.data;
  },

  rejectPendingUser: async (userId: string): Promise<void> => {
    await apiService.request({
      url: `/admin/pending-users/${userId}/reject`,
      method: 'POST'
    });
  },

  // --- Admin Calendar ---
  getAdminEvents: async (start?: string, end?: string): Promise<any[]> => {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const qs = params.toString();
    const response = await apiService.get<any[]>(`/admin/calendar/events${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  createAdminEvent: async (data: any): Promise<any> => {
    const response = await apiService.post<any>('/admin/calendar/events', data);
    return response.data;
  },

  updateAdminEvent: async (id: string, data: any): Promise<any> => {
    const response = await apiService.put<any>(`/admin/calendar/events/${id}`, data);
    return response.data;
  },

  deleteAdminEvent: async (id: string): Promise<void> => {
    await apiService.delete(`/admin/calendar/events/${id}`);
  },

  // --- Admin Financials ---
  getFinancials: async (year: number = 2026): Promise<any> => {
    const response = await apiService.get<any>(`/admin/financials?year=${year}`);
    return response.data;
  },

  getEcoCashDevices: async (): Promise<MerchantDevice[]> => {
    const response = await apiService.get<{ devices: MerchantDevice[] }>('/admin/ecocash/devices');
    return response.data.devices;
  },

  createEcoCashDevice: async (data: CreateMerchantDeviceData): Promise<MerchantDevice> => {
    const response = await apiService.post<MerchantDevice>('/admin/ecocash/devices', data);
    return response.data;
  },

  rotateEcoCashDeviceCredentials: async (deviceId: string): Promise<RotateMerchantCredentialsResponse> => {
    const response = await apiService.post<RotateMerchantCredentialsResponse>(
      `/payments/ecocash/devices/${deviceId}/rotate-credentials`,
      {}
    );
    return response.data;
  },

  getEcoCashAlerts: async (): Promise<EcoCashAdminAlert[]> => {
    const response = await apiService.get<EcoCashAdminAlert[]>('/admin/ecocash/alerts');
    return response.data;
  },

  getEcoCashReceipts: async (): Promise<EcoCashReceiptResponse[]> => {
    const response = await apiService.get<{ receipts: EcoCashReceiptResponse[] }>('/admin/ecocash/receipts');
    return response.data.receipts;
  },

  getEcoCashClaims: async (): Promise<EcoCashClaimResponse[]> => {
    const response = await apiService.get<{ claims: EcoCashClaimResponse[] }>('/admin/ecocash/claims');
    return response.data.claims;
  },

  reviewEcoCashClaim: async (
    claimId: string,
    action: 'approve' | 'reject' | 'manual_review',
    feedback_reason?: string
  ): Promise<EcoCashClaimResponse> => {
    const response = await apiService.post<EcoCashClaimResponse>(
      `/payments/ecocash/claims/${claimId}/review`,
      { action, feedback_reason }
    );
    return response.data;
  },

  createManualEcoCashReceipt: async (data: CreateManualEcoCashReceiptData): Promise<EcoCashReceiptResponse> => {
    const response = await apiService.post<EcoCashReceiptResponse>('/admin/ecocash/receipts/manual', data);
    return response.data;
  },

  getEcoCashAuditEvents: async (): Promise<Array<{
    id: string;
    actor_type: string;
    actor_user_id?: string;
    merchant_device_id?: string;
    entity_type: string;
    entity_id: string;
    event_type: string;
    message?: string;
    payload?: Record<string, unknown>;
    created_at: string;
  }>> => {
    const response = await apiService.get<{ events: Array<{
      id: string;
      actor_type: string;
      actor_user_id?: string;
      merchant_device_id?: string;
      entity_type: string;
      entity_id: string;
      event_type: string;
      message?: string;
      payload?: Record<string, unknown>;
      created_at: string;
    }> }>('/admin/ecocash/audit-events');
    return response.data.events;
  },
};
